import { Tab } from '@headlessui/react';
import { Account } from '@radixdlt/radix-dapp-toolkit';
import { LoaderFunctionArgs, json } from '@remix-run/cloudflare';
import { Link, NavLink, Outlet, useFetcher, useLoaderData } from '@remix-run/react';
import BigNumber from 'bignumber.js';
import { useEffect, useState } from 'react';
import AccountSelector from '~/components/account-selector';
import PopoverAPY from '~/components/popover-apy';
import { socialArrayToObject } from '~/components/socials';
import CGIcon from '~/components/socials/coingecko';
import DiscordIcon from '~/components/socials/discord';
import GithubIcon from '~/components/socials/github';
import TelegramIcon from '~/components/socials/telegram';
import TwitterIcon from '~/components/socials/twitter';
import TransactionButton from '~/components/transaction-button';
import { generateAddStakeManifest } from '~/lib/manifests';
import { getPairByBaseOrQuoteToken, getPairByBaseToken } from '~/lib/models/pair';
import { getTokenStakingByToken } from '~/lib/models/staking';
import { SelectToken, getToken } from '~/lib/models/tokens';
import { ROUND_UP, classNames, getIconUrl, onIconError, prettyValue, roundALR, shortenAddress } from '~/lib/util';
import { fetchTokenMetaData, fetchTokensDetails, useGatewayApi } from '~/rdt/gateway';
import { useRadix } from '~/rdt/radixProvider';
import { loader as walletLoader } from '~/routes/wallet.$account';
import { meta } from './_app.bridge';
import { setDbFromContext } from '~/lib/db';

export const sASTRL_RESOURCE_ADDRESS = 'resource_rdx1tkm0z9pkz0e59yf2258drzjwaq7axtcf2mupjz2ag9kwww4jvdfd6c';
export const STAKING_POOL_ADDRESS = 'pool_rdx1c325zs6dz3un8ykkjavy9fkvvyzarkaehgsl408qup6f95aup3le3w';


export async function loader({ params, context }: LoaderFunctionArgs) {
   setDbFromContext(context);

   if (!params?.address) {
      return json({
         error: 'Missing token',
         token: null,
         pairs: null,
      });
   }

   const [token] = await getToken(params.address);

   if (!token) {
      return json({
         error: 'Missing token',
         token: null,
         pairs: null,
      });
   }

   const pairs = await getPairByBaseOrQuoteToken(params.address);

   pairs.sort((a, b) => {
      // console.log(a.Pair.address, tvl[a.Pair.address]?.usdAmount, b.Pair.address, tvl[b.Pair.address]?.usdAmount);

      if (a.Pair.tvlUSD < b.Pair.tvlUSD) {
         return 1;
      } else if (a.Pair.tvlUSD > b.Pair.tvlUSD) {
         return -1;
      }

      return 0;
   });

   return json({
      error: null,
      token,
      pairs,
   });
}

export default function TokenStaking() {
   const { token, pairs } = useLoaderData<typeof loader>();
   
   return (
      <section className="flex items-top space-x-4">
         <div className="grow bg-primary-800 text-white rounded-xl lg:rounded-2xl space-y-4 p-8">
            <table className="min-w-full divide-y divide-gray-300">
               <thead>
                  <tr>
                     <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-primary-100 sm:pl-0">
                        Pair
                     </th>
                     <th scope="col" className="hidden px-3 pr-1 py-3.5 text-left text-sm font-semibold text-primary-100 sm:table-cell">
                        TVL
                     </th>
                     <th scope="col" className="hidden px-3 pl-1 py-3.5 text-left text-sm font-semibold text-primary-100 sm:table-cell"></th>
                     <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold text-primary-100 lg:table-cell">
                        <PopoverAPY title="7D ALR" timespan="This ALR is based on the data of the last seven days." />
                     </th>
                     <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold text-primary-100 lg:table-cell">
                        <PopoverAPY title="ALR" timespan="This ALR is based on the data since the creation of the pair." />
                     </th>
                     <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0"></th>
                  </tr>
               </thead>
               <tbody className="divide-y divide-primary-300">
             {pairs.map((pair) => {

                     return (
                       <tr key={pair.Token.address} className={classNames("hover:bg-background-900")}>
                         <td className="w-full max-w-0 py-4 pl-4 pr-3 font-bold sm:w-auto sm:max-w-none sm:pl-0">
                           <div className="flex items-center">
                             <div className="relative h-8 w-12">
                               <img className="absolute left-4 top-0 h-8 w-8 shrink-0 rounded-full bg-white" src={getIconUrl(pair.quoteToken.iconUrl)} alt="" />
                               <img className="absolute left-0 top-0 h-8 w-8 shrink-0 rounded-full bg-white" src={getIconUrl(pair.Token.iconUrl)} onError={(evt) => onIconError(evt)} alt="" />
                             </div>
                             <div className="ml-4">
                               {pair.Token.symbol} / {pair.quoteToken.symbol}
                             </div>
                           </div>
                         </td>
                         <td className="text-primary-200 hidden px-3 py-4 pr-1 text-sm font-medium sm:table-cell">
                           <p className="flex space-x-2">
                             <img className="h-4 w-4 shrink-0 rounded-full bg-white" src={getIconUrl(pair.Token.iconUrl, 16)} onError={(evt) => onIconError(evt)} alt="" />
                             <span>
                               {prettyValue(pair.Pair.baseTVL || 0)} {pair.Token.symbol}
                             </span>
                           </p>
                           <p className="flex space-x-2">
                             <img className="h-4 w-4 shrink-0 rounded-full bg-white" src={getIconUrl(pair.quoteToken.iconUrl, 16)} alt="" />
                             <span>
                               {prettyValue(pair.Pair.quoteTVL || 0)} {pair.quoteToken.symbol}
                             </span>
                           </p>
                         </td>
                         <td className="text-primary-200 hidden px-3 py-4 pl-1 font-medium sm:table-cell">${prettyValue(pair.Pair.tvlUSD || 0)}</td>
                         <td className="text-primary-200 hidden px-3 py-4 pr-1 text-sm font-medium sm:table-cell">
                           <p className="flex space-x-2">
                             <img className="h-4 w-4 shrink-0 rounded-full bg-white" src={getIconUrl(pair.Token.iconUrl, 16)} onError={(evt) => onIconError(evt)} alt="" />
                             <span>{roundALR(pair.Pair.baseAPY7D)}%</span>
                           </p>
                           <p className="flex space-x-2">
                             <img className="h-4 w-4 shrink-0 rounded-full bg-white" src={getIconUrl(pair.quoteToken.iconUrl, 16)} alt="" />
                             <span>{roundALR(pair.Pair.quoteAPY7D)}%</span>
                           </p>
                         </td>
                         <td className="text-primary-200 hidden px-3 py-4 pr-1 text-sm font-medium sm:table-cell">
                           <p className="flex space-x-2">
                             <img className="h-4 w-4 shrink-0 rounded-full bg-white" src={getIconUrl(pair.Token.iconUrl, 16)} onError={(evt) => onIconError(evt)} alt="" />
                             <span>{roundALR(pair.Pair.baseAPY)}%</span>
                           </p>
                           <p className="flex space-x-2">
                             <img className="h-4 w-4 shrink-0 rounded-full bg-white" src={getIconUrl(pair.quoteToken.iconUrl, 16)} alt="" />
                             <span>{roundALR(pair.Pair.quoteAPY)}%</span>
                           </p>
                         </td>
                         <td className="py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                           <NavLink
                             to={`/liquidity/add/${pair.Pair.address}?direction=base`}
                             className={({ isActive }) =>
                               isActive
                                 ? "text-md text-primary-100 bg-primary-600 flex items-center justify-center rounded-full py-3 font-medium uppercase leading-none tracking-wider"
                                 : "text-md hover:bg-primary-500 hover:border-primary-500 rounded-full border border-white px-4 py-3 font-medium uppercase leading-none tracking-wider text-white"
                             }
                           >
                             {({ isActive, isPending }) => (
                               <>
                                 {isActive ? (
                                   <>
                                     {/* <span>Adding</span> */}
                                     <svg className="h-4 w-4" viewBox="2 2 16 16" xmlns="http://www.w3.org/2000/svg">
                                       <path
                                         d="M10.986 5.456a.608.608 0 0 0-.827 0 .564.564 0 0 0 0 .809l3.233 3.16H5.29a.579.579 0 0 0-.584.572.58.58 0 0 0 .584.572h8.102l-3.233 3.16a.564.564 0 0 0 0 .81c.11.107.257.167.414.167.155 0 .302-.06.413-.168l4.072-3.982a.778.778 0 0 0 0-1.119l-4.072-3.98z"
                                         fill="currentColor"
                                         fillRule="nonzero"
                                       ></path>
                                     </svg>
                                   </>
                                 ) : (
                                   "Add"
                                 )}
                               </>
                             )}
                           </NavLink>
                         </td>
                       </tr>
                     );
                  })}
               </tbody>
            </table>
         </div>
      </section>
   );
}
